import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { useForm, useFieldArray } from "react-hook-form";

import AddMessage from "../components/AddMessage";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import SendRecipientCard from "./SendRecipientCard";
import { useSendTemplateMutation } from "../services/useSendTemplateMutation";
import { toast } from "react-toastify";

const SendTemplateManualPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [templateData, setTemplateData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    mutateAsync: sendTemplate,
    isLoading: sendLoading,
    isError,
    error: sendError,
    isSuccess,
  } = useSendTemplateMutation();
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    control,
    formState: { errors },
    watch,
    reset,
  } = useForm({});

  const { fields, append, remove } = useFieldArray({
    control,
    name: "recipients",
    defaultValues: {
      recipients: [],
    },
  });

  const sortedFields = fields.sort((a, b) => a.signing_order - b.signing_order);

  useEffect(() => {
    setIsLoading(true);
    setError(null);

    const loadData = () => {
      if (location.state && location.state.data) {
        const receivedData = location.state.data;
        const formattedData = {
          templateId: receivedData.id,
          templateName: receivedData.name,
          templateDescription: receivedData.description,
          emailSubject: receivedData.email_subject,
          emailMessage: receivedData.email_message,
          webhookUrl: receivedData.webhook_url,
        };

        setTemplateData(formattedData);

        reset((prevValues) => ({
          ...prevValues,
          ...formattedData,
          recipients: [],
        }));

        if (receivedData?.email_sent === 1 && receivedData?.total_signers) {
          const signersToShow = receivedData.signers.slice(
            0,
            receivedData.total_signers
          );

          signersToShow.forEach((signer) => {
            append({
              id: signer.id,
              name: "",
              email: "",
              is_signed: signer.is_signed,
              date_signed: signer.date_signed,
              date_created: signer.date_created,
              signer_roles: signer.signer_roles,
              signing_order: signer.signing_order,
              signer_role_name: signer.signer_role_name,
            });
          });
        } else {
          if (receivedData?.signers?.length > 0) {
            receivedData.signers.forEach((signer) => append(signer));
          }
        }
      } else {
        setError("No template data provided.");
      }
      setIsLoading(false);
    };

    loadData();
  }, [location, reset, append]);

  const onSubmit = async (data) => {
    const sortedRecipients =
      data?.recipients
        ?.sort((a, b) => a.signing_order - b.signing_order)
        ?.map((recipient) => ({
          ...recipient,
          signer_roles: recipient.signer_roles ?? 0,
        })) ?? [];

    const payload = {
      id: templateData?.templateId,
      email_subject: data?.emailSubject,
      email_message: data?.emailMessage,
      webhook_url: data?.webhookUrl,
      signers: sortedRecipients,
    };

    const res = await sendTemplate(payload);
    if (res?.success) {
      toast.success("Email Send to Signer Successfully");
      navigate("/");
    } else {
      toast.error(res?.data?.[0]?.msg || "Error sending email to signer");
    }
  };

  const handleClear = () => {
    reset(templateData);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleDiscard = () => {
    reset(templateData);
    navigate("/");
  };

  const handleSaveAndClose = () => {
    handleSubmit(onSubmit)();
    handleCloseModal();
    navigate("/");
  };

  if (isLoading) {
    return <div>Loading template...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!templateData) {
    return <div>Template not found or could not be loaded.</div>;
  }

  const sortedWatch = watch("recipients").sort(
    (a, b) => a.signing_order - b.signing_order
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="min-h-screen flex flex-col bg-gray-100">
        <div className="sticky top-0 bg-white shadow-md p-4 flex justify-between items-center z-10 border-b border-gray-400">
          <h2 className="text-lg font-semibold">Send Template</h2>
          <div className="flex items-center">
            <button
              onClick={handleOpenModal}
              className="focus:outline-none rounded-full p-2 hover:bg-gray-200 transition-colors duration-200"
            >
              <FontAwesomeIcon icon={faTimes} className="text-gray-600" />
            </button>
          </div>
        </div>

        <div className="container mx-auto p-4 md:p-8 flex-grow">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="rounded-lg shadow-md overflow-hidden max-w-2xl mx-auto">
              <div className="p-6">
                {sortedFields.map((field, index) => (
                  <SendRecipientCard
                    key={field.signing_order || `recipients-${index}`}
                    recipient={field}
                    index={index}
                    remove={remove}
                    register={register}
                    errors={errors}
                    control={control}
                    watch={sortedWatch}
                    setValue={setValue}
                  />
                ))}
              </div>
            </div>

            <div className="rounded-lg shadow-md overflow-hidden max-w-2xl mx-auto">
              <div className="p-6">
                <AddMessage
                  register={register}
                  errors={errors}
                  watch={watch}
                  control={control}
                />
              </div>
            </div>
          </form>
        </div>

        <div className="sticky bottom-0 bg-white shadow-md p-4 flex justify-end items-center z-10 border-t border-gray-300">
          <button
            type="button"
            onClick={handleSubmit(onSubmit)}
            className="bg-[#cfc9ff] text-black hover:bg-[#948ae3] py-2 px-4 rounded focus:outline-none focus:shadow-outline "
            disabled={sendLoading}
          >
            {sendLoading ? "Sending..." : "Send"}
          </button>
        </div>
        {isModalOpen && (
          <div className="fixed top-0 left-0 w-full h-full bg-gray-500 bg-opacity-75 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full sm:max-w-sm">
              <div className="flex justify-end">
                <button
                  onClick={handleCloseModal}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <h2 className="text-lg font-semibold mb-4">
                Discard your changes?
              </h2>
              <p className="text-gray-700 mb-4">
                Do you want to discard changes to this template?
              </p>
              <div className="mt-4 flex justify-end space-x-4">
                <button
                  onClick={handleDiscard}
                  className="bg-[#cfc9ff] text-black hover:bg-[#948ae3] py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Discard Changes
                </button>
                {/* <button
                  onClick={handleSaveAndClose}
                  className="bg-[#cfc9ff] text-black hover:bg-[#948ae3] py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Save
                </button> */}
              </div>
            </div>
          </div>
        )}
      </div>
    </DndProvider>
  );
};

export default SendTemplateManualPage;
